import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  consumeFollowForAfterAuth,
  consumeRegisterForAfterAuth,
  consumeSubscribeForOrganizerOfAfterAuth,
} from '@/lib/registerForIntent';
import { postLoginPath } from '@/lib/appSurface';
import { safeRedirectOrDefault } from '@/lib/safeRedirect';
import { useTranslation } from 'react-i18next';

const ERROR_REDIRECT_MS = 5_000;

export function AuthCallbackPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function handleCallback() {
      // Handle both email verification and OAuth callback
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');

      if (code) {
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        if (exchangeError) {
          setError(exchangeError.message);
          return;
        }
      }

      // Defense in depth against open-redirect: never hand `navigate` a
      // path we haven't validated, even when the source is an internal
      // helper. A future helper change that constructs an unsafe path
      // gets caught here instead of phishing a real user.
      const safeNavigate = (path: string) =>
        navigate(safeRedirectOrDefault(path, postLoginPath()), {
          replace: true,
        });

      const registerFor = params.get('registerFor');
      if (registerFor) {
        await consumeRegisterForAfterAuth(registerFor, safeNavigate);
        return;
      }
      const followFor = params.get('followFor');
      if (followFor) {
        await consumeFollowForAfterAuth(followFor, safeNavigate);
        return;
      }
      const subscribeForOrganizerOf = params.get('subscribeForOrganizerOf');
      if (subscribeForOrganizerOf) {
        await consumeSubscribeForOrganizerOfAfterAuth(
          subscribeForOrganizerOf,
          safeNavigate,
        );
        return;
      }

      // Redirect to the post-login surface (director vs. player based on hostname).
      navigate(postLoginPath());
    }

    handleCallback();
  }, [navigate]);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => navigate('/login', { replace: true }), ERROR_REDIRECT_MS);
    return () => clearTimeout(timer);
  }, [error, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md" data-testid="callback-error">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{t('callback.error_title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <p className="text-center text-sm text-muted-foreground">
              {t('callback.redirect_hint')}
            </p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">{t('callback.go_to_login')}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4" data-testid="callback-loading">
      <div className="flex flex-col items-center gap-4 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-muted-foreground">{t('callback.completing')}</p>
      </div>
    </div>
  );
}
